import {
  FC,
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react'

import { Episode, Strings } from 'models'
import { config } from 'config'
import { GlobalStyle } from 'styles/global'
import { Loader } from 'components/loader'
import { fetchData } from 'utils'

type DataContextType = {
  episodes: Episode[]
  strings: Strings
  appLocale: typeof config.locale
}

const DataContext = createContext<DataContextType | null>(null)

export const DataProvider: FC<{
  children?: ReactNode | ReactNode[]
}> = ({ children }) => {
  const params = new URLSearchParams(window.location.search)
  const appLocale = (params.get('lang') ||
    config.locale) as typeof config.locale
  const [episodes, setEpisodes] = useState<Episode[]>([])
  const [strings, setStrings] = useState<Strings | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('development mode')

      fetchData()
        .then(json => {
          console.log('data:', json)
          setEpisodes(json.data)
          setStrings(json.strings)
        })
        .catch(console.log)
        .finally(() => setIsLoading(false))
    } else {
      console.log('production mode')

      fetch(`${process.env.PUBLIC_URL}/static/data.json`)
        .then(data => data.json())
        .then(json => {
          console.log('data:', json)
          setEpisodes(json.data)
          setStrings(json.strings)
        })
        .catch(console.log)
        .finally(() => setIsLoading(false))
    }
  }, [])

  return isLoading || !episodes || !strings ? (
    <>
      <GlobalStyle />
      <Loader />
    </>
  ) : (
    <DataContext.Provider
      value={{
        episodes,
        strings,
        appLocale,
      }}
    >
      {children}
    </DataContext.Provider>
  )
}

export const useDataContext = () => {
  const context = useContext(DataContext)
  return context as DataContextType
}
